import React, { PropsWithChildren, useMemo, useState } from "react";

import CircleIcon from "@mui/icons-material/Circle";
import UploadIcon from "@mui/icons-material/Upload";

import { PortfolioPlanId } from "@fartherfinance/frontend/api/Types";

import CSVUploadModalLauncher from "../../../CreatePortfolioUploadModal/ModalLauncher";
import { usePortfolioBuilderContext } from "../../reducer/Context";
import { getNumberOfSleevesOrSecurities } from "../../reducer/reducer";
import Button from "@src/multiCustodian/components/MUI/Button/Button";
import Text from "@src/sharedComponents/Typography/Text";
import { pluralize } from "@src/utils/pluralize";

import styles from "./TableAndSearchBarHeader.module.css";

interface Props {
  portfolioPlanId: PortfolioPlanId | null;
}

const TableAndSearchBarHeader: React.FC<PropsWithChildren<Props>> = ({
  portfolioPlanId,
  children,
}) => {
  const [showCSVUploadModal, setShowCSVUploadModal] = useState<boolean>(false);

  const onShowCSVUploadModal = () => setShowCSVUploadModal(true);
  const onCloseCSVUploadModal = () => setShowCSVUploadModal(false);

  const { state } = usePortfolioBuilderContext();

  const numberSleeves = useMemo(
    () => getNumberOfSleevesOrSecurities("Sleeve", state.modelTable),
    [state.modelTable]
  );

  const numberSecurities = useMemo(
    () => getNumberOfSleevesOrSecurities("Security", state.modelTable),
    [state.modelTable]
  );

  return (
    <>
      <CSVUploadModalLauncher
        showModal={showCSVUploadModal}
        closeModal={onCloseCSVUploadModal}
        portfolioPlanId={portfolioPlanId ?? undefined}
      />

      <div className={styles.container}>
        <div className={styles.flexRow}>
          <Text className={styles.header} variant="title2" color={"text"}>
            {state.modelName}
          </Text>

          <Text variant="body" color={"textSubtle"}>
            {pluralize(
              state.modelTable.filter((ac) => !ac.isInvisible).length,
              "asset class",
              true
            )}
          </Text>

          <CircleIcon className={styles.dot} />

          <Text variant="body" color={"textSubtle"}>
            {pluralize(numberSleeves, "sleeve", true)}
          </Text>

          <CircleIcon className={styles.dot} />

          <Text variant="body" color={"textSubtle"}>
            {pluralize(numberSecurities, "security", true)}
          </Text>
        </div>

        <div className={styles.flexRow}>
          <Button
            text="Upload"
            onClick={onShowCSVUploadModal}
            variant="text"
            buttonType="primary"
            endIcon={<UploadIcon sx={{ width: 18, height: 18 }} />}
            style={{ whiteSpace: "nowrap" }}
            sx={{ width: "134px", height: "32px" }}
          />

          {children}
        </div>
      </div>
    </>
  );
};

export default TableAndSearchBarHeader;
